/**
 * Pure helpers for the MatchupMatrixModal — extracted into a non-JSX file
 * so unit tests can import them without dragging the React + Emotion
 * rendering pipeline through Jest's babel transform.
 *
 * The modal itself imports from here; tests import from here. Keep this
 * file free of React imports so the surface stays trivially testable.
 */

import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'

/**
 * Per-pair count override map. Key: '<uid_a>|<uid_b>' canonical-sorted.
 * Value: integer game count for that pair.
 */
export type MatchupMatrix = Record<string, number>

/**
 * Build a canonical pair key. Sorted lexicographically so the key for
 * (A, B) is identical to (B, A) — required for symmetry and matches the
 * scheduler-python validator's expectation.
 */
export const pairKey = (uidA: string, uidB: string): string =>
  [uidA, uidB].sort().join('|')

/**
 * Build the matrix-shape view of the existing pair_matchup_count constraints
 * for this division (canonical pair key → count). Skips malformed rows
 * (wrong type code, wrong team_uids count, non-numeric count) so callers
 * see a clean dict.
 */
export function constraintsToMatrix(
  constraints: SchedulerConstraint[]
): MatchupMatrix {
  const matrix: MatchupMatrix = {}
  for (const c of constraints) {
    if (c.type.code !== 'pair_matchup_count') {
      continue
    }
    const teamUids = c.team_uids ?? []
    if (teamUids.length !== 2) {
      continue
    }
    const count = c.parameters.count
    if (typeof count !== 'number') {
      continue
    }
    matrix[pairKey(teamUids[0], teamUids[1])] = count
  }
  return matrix
}

/**
 * Build a lookup from canonical pair key to existing constraint id, so we
 * can route each grid cell change to add/update/delete.
 */
export function constraintsToIdMap(
  constraints: SchedulerConstraint[]
): Record<string, string> {
  const map: Record<string, string> = {}
  for (const c of constraints) {
    if (c.type.code !== 'pair_matchup_count') {
      continue
    }
    const teamUids = c.team_uids ?? []
    if (teamUids.length !== 2) {
      continue
    }
    map[pairKey(teamUids[0], teamUids[1])] = c.id
  }
  return map
}

/**
 * Set of canonical pair keys whose existing constraint is "global" —
 * i.e. has division_uids === null (or empty). Used by the matrix modal
 * to seed the per-cell "Apply to all divisions" toggle and to detect
 * scope changes during the save diff.
 */
export function constraintsToGlobalKeys(
  constraints: SchedulerConstraint[]
): Set<string> {
  const out = new Set<string>()
  for (const c of constraints) {
    if (c.type.code !== 'pair_matchup_count') {
      continue
    }
    const teamUids = c.team_uids ?? []
    if (teamUids.length !== 2) {
      continue
    }
    if (c.division_uids === null || c.division_uids.length === 0) {
      out.add(pairKey(teamUids[0], teamUids[1]))
    }
  }
  return out
}

/**
 * Save operation kinds emitted by computeSaveOperations. The handler maps
 * each to the corresponding constraints API call (addConstraint /
 * updateConstraint / deleteConstraint).
 *
 * `divisionUids` semantics:
 *   - `null` → constraint applies to all divisions (a "global" override)
 *   - `[divisionUid]` → constraint applies only to that one division
 */
export type MatchupSaveOperation =
  | {
      kind: 'add'
      teamUids: [string, string]
      count: number
      divisionUids: string[] | null
    }
  | {
      kind: 'update'
      constraintId: string
      count: number
      /** Only set when the user toggled the cell's scope (per-division ↔ global). */
      divisionUids?: string[] | null
    }
  | { kind: 'delete'; constraintId: string }

export interface SaveDiffInputs {
  /** Desired count per pair key. Pairs not present here will be deleted. */
  desiredMatrix: MatchupMatrix
  /** Subset of `desiredMatrix` keys the user marked as global. */
  desiredGlobalKeys: Set<string>
  /** All pair_matchup_count constraints currently visible in this matrix view. */
  existingConstraints: SchedulerConstraint[]
  /** Division currently being edited — used as the scope for non-global cells. */
  currentDivisionUid: string
}

/**
 * Pure diff function: given the desired override matrix and the existing
 * pair_matchup_count constraints, return the list of ops needed to
 * reconcile.
 *
 *   - Pair in matrix but no existing constraint → add
 *   - Pair in matrix with existing constraint, count differs → update
 *   - Pair in matrix with existing constraint, scope differs → update with new divisionUids
 *   - Pair in matrix with existing constraint, same count + scope → no-op
 *   - Existing constraint, pair NOT in matrix → delete (reset to default)
 *
 * No side effects, no API calls — caller applies the ops via
 * addConstraint/updateConstraint/deleteConstraint.
 */
export function computeSaveOperations(
  inputs: SaveDiffInputs
): MatchupSaveOperation[] {
  const {
    desiredMatrix,
    desiredGlobalKeys,
    existingConstraints,
    currentDivisionUid,
  } = inputs

  const ops: MatchupSaveOperation[] = []
  const existingIdByKey = constraintsToIdMap(existingConstraints)
  const existingCountByKey = constraintsToMatrix(existingConstraints)
  const existingGlobalKeys = constraintsToGlobalKeys(existingConstraints)

  // Adds and updates
  for (const [key, count] of Object.entries(desiredMatrix)) {
    const [uidA, uidB] = key.split('|') as [string, string]
    const wantsGlobal = desiredGlobalKeys.has(key)
    const desiredScope: string[] | null = wantsGlobal
      ? null
      : [currentDivisionUid]
    const existingId = existingIdByKey[key]
    if (existingId === undefined) {
      ops.push({
        kind: 'add',
        teamUids: [uidA, uidB],
        count,
        divisionUids: desiredScope,
      })
      continue
    }
    const countChanged = existingCountByKey[key] !== count
    const scopeChanged = existingGlobalKeys.has(key) !== wantsGlobal
    if (countChanged || scopeChanged) {
      const op: MatchupSaveOperation = {
        kind: 'update',
        constraintId: existingId,
        count,
      }
      if (scopeChanged) {
        op.divisionUids = desiredScope
      }
      ops.push(op)
    }
  }

  // Deletes (existing pair no longer in desired matrix)
  for (const [key, existingId] of Object.entries(existingIdByKey)) {
    if (!(key in desiredMatrix)) {
      ops.push({ kind: 'delete', constraintId: existingId })
    }
  }

  return ops
}

/**
 * Per-division plan emitted by the modal's save flow — the ops list
 * for one division plus the desired override count (used for toast
 * copy and feasibility KPIs).
 */
export interface MatchupSavePlan {
  divisionId: string
  ops: MatchupSaveOperation[]
  desiredCount: number
}

/**
 * Bulk-save payload shape consumed by `useSchedulerConstraints.bulkSaveConstraints`.
 * Pulled out as a pure helper so the per-team-mode and division-total-mode
 * inputs can be round-trip checked without mounting the modal.
 */
export interface MatchupBulkSavePayload {
  add: {
    constraint_type_code: 'pair_matchup_count'
    parameters: { count: number }
    team_uids: [string, string]
    division_uids: string[] | null
    is_hard: true
    is_enabled: true
  }[]
  update: {
    id: string
    data: {
      parameters: { count: number }
      division_uids?: string[] | null
    }
  }[]
  delete: string[]
}

/**
 * Collapse a list of per-division plans into the `{add, update, delete}`
 * shape the bulk endpoint expects. Mirrors what the modal used to do
 * inline before the bulk endpoint existed.
 */
export function buildBulkSavePayload(
  plans: MatchupSavePlan[]
): MatchupBulkSavePayload {
  const add: MatchupBulkSavePayload['add'] = []
  const update: MatchupBulkSavePayload['update'] = []
  const del: string[] = []
  for (const plan of plans) {
    for (const op of plan.ops) {
      if (op.kind === 'add') {
        add.push({
          constraint_type_code: 'pair_matchup_count',
          parameters: { count: op.count },
          team_uids: [op.teamUids[0], op.teamUids[1]],
          division_uids: op.divisionUids,
          is_hard: true,
          is_enabled: true,
        })
      } else if (op.kind === 'update') {
        const data: {
          parameters: { count: number }
          division_uids?: string[] | null
        } = {
          parameters: { count: op.count },
        }
        if (op.divisionUids !== undefined) {
          data.division_uids = op.divisionUids
        }
        update.push({ id: op.constraintId, data })
      } else {
        del.push(op.constraintId)
      }
    }
  }
  return { add, update, delete: del }
}

/**
 * Compute per-team game counts from a matrix + uniform default.
 * Each team's total is the sum across all pairs it appears in.
 *
 * Used by the modal's per-team panel + imbalance warning.
 */
export function computePerTeamCounts(
  teams: { id: string }[],
  matrix: MatchupMatrix,
  gamesPerMatchup: number
): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const t of teams) {
    counts[t.id] = 0
  }
  for (let i = 0; i < teams.length; i += 1) {
    for (let j = i + 1; j < teams.length; j += 1) {
      const key = pairKey(teams[i].id, teams[j].id)
      const c = matrix[key] ?? gamesPerMatchup
      counts[teams[i].id] += c
      counts[teams[j].id] += c
    }
  }
  return counts
}

/**
 * Sum the per-pair matchup counts for a division. Pairs not present in the
 * matrix fall back to the uniform `gamesPerMatchup`. Mirrors the same
 * formula scheduler-python uses to size the schedule.
 */
export function computeTotalGames(
  teams: { id: string }[],
  matrix: MatchupMatrix,
  gamesPerMatchup: number
): number {
  if (teams.length < 2) {
    return 0
  }
  let total = 0
  for (let i = 0; i < teams.length; i += 1) {
    for (let j = i + 1; j < teams.length; j += 1) {
      const key = pairKey(teams[i].id, teams[j].id)
      total += matrix[key] ?? gamesPerMatchup
    }
  }
  return total
}

/**
 * Effective per-team and total game counts for a division (SN-7897).
 *
 * When the admin has set an EXPLICIT per-team target it is authoritative: a
 * partial round-robin (target < opponents, e.g. 3 games with 8 teams) is valid
 * and the backend resolves balanced per-pair counts from it. The legacy
 * `matrix`/`gamesPerMatchup` representation can only express a FULL round-robin
 * or above (`gamesPerMatchup >= 1`), so it MUST NOT be used to derive the value
 * for an explicit target — doing so clamps e.g. 3 up to a full round-robin (7).
 * Total games = floor(teamCount * perTeam / 2) (each game uses two team-slots).
 *
 * With no explicit target (e.g. a division seeded from existing
 * pair_matchup_count rows), fall back to the matrix-derived value, which is
 * always at least a full round-robin and which the matrix can represent.
 */
export function divisionGamesSummary(
  teams: { id: string }[],
  explicitPerTeamTarget: number | null,
  matrix: MatchupMatrix,
  gamesPerMatchup: number
): { perTeam: number; divGames: number } {
  const teamCount = teams.length
  if (explicitPerTeamTarget != null) {
    return {
      perTeam: explicitPerTeamTarget,
      // CEIL — matches the backend (target_resolution.division_total_for_target).
      // When teamCount * target is odd (5 teams x 25) the halves don't divide;
      // rounding DOWN buys one game too few and strands a team below the target
      // the customer asked for. Rounding up gives everyone their N, with a single
      // unavoidable team at N+1.
      divGames: Math.ceil((teamCount * explicitPerTeamTarget) / 2),
    }
  }
  const opponents = Math.max(0, teamCount - 1)
  let perTeam = opponents * gamesPerMatchup
  if (Object.keys(matrix).length > 0 && teamCount >= 2) {
    const counts = computePerTeamCounts(teams, matrix, gamesPerMatchup)
    perTeam = Math.max(...Object.values(counts), 0)
  }
  return {
    perTeam,
    divGames: computeTotalGames(teams, matrix, gamesPerMatchup),
  }
}

/**
 * Result of computing an auto-balanced override layout for a per-team
 * games target. Built so the modal can apply it (or warn cleanly) without
 * any graph theory leaking into the JSX.
 */
export interface AutoBalanceResult {
  /** Base games_per_matchup that all unboosted pairs will play. */
  baseGamesPerMatchup: number
  /**
   * Pairs (canonical sorted pairKey) that should receive +1 game on top
   * of the base. Length is `(N * remainder) / 2` when achievable.
   */
  boostedPairKeys: string[]
  /**
   * True when the target distributes evenly — every team ends up with
   * exactly `target` games. False when the parity makes a perfect
   * solution impossible (N*remainder is odd).
   */
  achievable: boolean
  /**
   * The closest uniform per-team value we can reach without overrides.
   * Used by the modal to show "closest are X or Y" when achievable=false.
   */
  closestUniformBelow: number
  closestUniformAbove: number
}

/**
 * Generate the per-pair boosts needed so every team plays exactly
 * `targetGamesPerTeam` games in a round-robin format with `teamIds.length`
 * teams. Returns null when the target is below the floor of 0 or the
 * input is degenerate.
 *
 * Strategy:
 *   1. base = floor(target / (N - 1)). Every pair plays this many games.
 *   2. remainder = target - base * (N - 1). Each team needs this many
 *      ADDITIONAL games, distributed across `(N * remainder) / 2` boosted
 *      pairs (each boost adds +1 to two teams).
 *   3. Build an `r`-regular graph on the team set using a circulant
 *      construction: connect team i to teams i+1..i+r/2 (and i+N/2 when
 *      r is odd, only valid when N is even).
 *   4. When N*r is odd (N odd AND r odd), no perfect solution exists —
 *      return achievable=false with the closest uniform values so the
 *      caller can communicate the constraint.
 *
 * The circulant construction is well-known to produce r-regular graphs
 * for any (N, r) where N*r is even and r < N.
 */
export function buildAutoBalanceOverrides(
  teamIds: string[],
  targetGamesPerTeam: number
): AutoBalanceResult | null {
  const n = teamIds.length
  if (n < 2 || targetGamesPerTeam < 0) {
    return null
  }
  const opponentsPerTeam = n - 1
  const base = Math.max(0, Math.floor(targetGamesPerTeam / opponentsPerTeam))
  const remainder = targetGamesPerTeam - base * opponentsPerTeam
  const closestUniformBelow = base * opponentsPerTeam
  const closestUniformAbove = (base + 1) * opponentsPerTeam

  if (remainder === 0) {
    return {
      baseGamesPerMatchup: base,
      boostedPairKeys: [],
      achievable: true,
      closestUniformBelow,
      closestUniformAbove,
    }
  }

  // Parity gate: N * r must be even for a perfect r-regular distribution.
  if ((n * remainder) % 2 !== 0) {
    return {
      baseGamesPerMatchup: base,
      boostedPairKeys: [],
      achievable: false,
      closestUniformBelow,
      closestUniformAbove,
    }
  }

  // Circulant construction. Indices into teamIds are the graph nodes.
  const edgeSet = new Set<string>()
  const half = Math.floor(remainder / 2)
  for (let i = 0; i < n; i += 1) {
    for (let k = 1; k <= half; k += 1) {
      const j = (i + k) % n
      const a = Math.min(i, j)
      const b = Math.max(i, j)
      edgeSet.add(`${a}|${b}`)
    }
  }
  // When `remainder` is odd, add the antipodal edges (only well-defined
  // when N is even — and N*remainder being even with odd remainder
  // implies N is even).
  if (remainder % 2 === 1) {
    const halfN = n / 2
    for (let i = 0; i < halfN; i += 1) {
      const j = i + halfN
      const a = Math.min(i, j)
      const b = Math.max(i, j)
      edgeSet.add(`${a}|${b}`)
    }
  }

  const boostedPairKeys: string[] = []
  for (const edgeKey of edgeSet) {
    const [aIdx, bIdx] = edgeKey.split('|').map(Number)
    boostedPairKeys.push(pairKey(teamIds[aIdx], teamIds[bIdx]))
  }

  return {
    baseGamesPerMatchup: base,
    boostedPairKeys,
    achievable: true,
    closestUniformBelow,
    closestUniformAbove,
  }
}

// ---------------------------------------------------------------------------
// Feasibility estimation
//
// The Matchup Config modal asks one question — "how many games per team?" —
// and tells the user whether the answer is achievable given the available
// time slots. This estimate is conservative: raw concrete-slot count vs.
// required matchups, no facility/per-day refinement. The scheduler-python
// partition pass remains authoritative; this client-side check is just so
// the user gets feedback before they hit Save / Generate.
// ---------------------------------------------------------------------------

/**
 * Count concrete slot-instances within `[startDate, endDate]` (inclusive)
 * for the given active time-slot templates.
 *
 * Mirrors scheduler-python's `expand_time_slot_templates` at a coarse
 * grain: each template emits one game-slot per (date, time-range) within
 * the range. Recurrence semantics:
 *   - 'specific'   → 1 instance if `slot_date` falls in range; else 0
 *   - 'weekly'     → for each `days[]` entry, count matching weekdays in
 *                    the range × `time_ranges.length`
 *   - 'biweekly'   → like weekly but every other week (start at first
 *                    matching weekday)
 *   - 'monthly'    → for each `days[]` entry, count one match per month in
 *                    range × `time_ranges.length`
 *
 * Returns 0 for an empty / invalid range.
 */
export function expandSlotsCount(
  timeSlots: TimeSlot[],
  startDate: string,
  endDate: string
): number {
  if (!startDate || !endDate) {
    return 0
  }
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0
  }
  if (end.getTime() < start.getTime()) {
    return 0
  }
  const oneDayMs = 86_400_000
  const totalDays = Math.floor((end.getTime() - start.getTime()) / oneDayMs) + 1

  let total = 0
  for (const slot of timeSlots) {
    if (!slot.is_active) {
      continue
    }
    const trCount = slot.time_ranges?.length ?? 0
    if (trCount === 0) {
      continue
    }
    if (slot.recurrence_type === 'specific') {
      const date = slot.slot_date
      if (!date) {
        continue
      }
      if (date >= startDate && date <= endDate) {
        total += trCount
      }
      continue
    }
    const days = slot.days ?? []
    if (days.length === 0) {
      continue
    }
    if (slot.recurrence_type === 'weekly') {
      // Count matching weekdays in the range.
      for (const dow of days) {
        total += countWeekdaysInRange(start, totalDays, dow) * trCount
      }
      continue
    }
    if (slot.recurrence_type === 'biweekly') {
      for (const dow of days) {
        total +=
          Math.ceil(countWeekdaysInRange(start, totalDays, dow) / 2) * trCount
      }
      continue
    }
    if (slot.recurrence_type === 'monthly') {
      // One match per month per `days[]` entry, ignoring nth-of-month
      // semantics (we don't have that detail in the template). Coarse.
      const monthsInRange = countMonthsInRange(start, end)
      total += days.length * monthsInRange * trCount
    }
  }
  return total
}

/**
 * Count the number of times `dow` (0=Sun..6=Sat) occurs in the
 * `totalDays`-long window starting at `start`.
 */
function countWeekdaysInRange(
  start: Date,
  totalDays: number,
  dow: number
): number {
  if (totalDays <= 0) {
    return 0
  }
  const startDow = start.getDay()
  // Days from start to first matching weekday (0..6).
  const offset = (dow - startDow + 7) % 7
  if (offset >= totalDays) {
    return 0
  }
  return Math.floor((totalDays - offset - 1) / 7) + 1
}

/** Inclusive month-count between two dates. */
function countMonthsInRange(start: Date, end: Date): number {
  const months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth())
  return Math.max(0, months) + 1
}

export interface FeasibilityInput {
  /** Number of teams in the division being checked. */
  divisionTeamCount: number
  /** User's target games-per-team for this division. */
  gamesPerTeam: number
  /**
   * Total concrete slot-instances available across all facilities for the
   * flight's date range. From `expandSlotsCount()`.
   */
  totalAvailableSlots: number
  /**
   * Slots already accounted for by *other* divisions in this flight. Pass
   * the running sum so each division's check accounts for the others'
   * draws on the shared slot pool.
   */
  alreadyAllocatedSlots?: number
  /**
   * Team UIDs in the division.
   *
   * No longer read by `checkFeasibility`: it used to run these through
   * `buildAutoBalanceOverrides` to decide whether a PERFECTLY r-regular
   * distribution existed, and reject the target when one didn't. The backend now
   * rounds an odd division total UP instead of refusing it, so that gate is gone
   * (see `checkFeasibility`). The field stays on the input contract — it
   * identifies the division being checked and every call site already supplies
   * it — but nothing in the feasibility path depends on it today.
   */
  teamUids: string[]
}

export type FeasibilityResult =
  | {
      kind: 'feasible'
      /**
       * Where the numbers came from. `'backend'` is the authoritative
       * scheduler verdict; `'estimate'` is the local pre-backend /
       * network-failure fallback — the chip must SAY so instead of rendering
       * a confident green (D4, 2026-07 audit; the infeasible variant already
       * carried this).
       */
      source: 'backend' | 'estimate'
      /**
       * Backend-only: true when scheduler-python stamped the response
       * `feasibility_degraded` — its partition refinement failed and the
       * verdict fell back to OPTIMISTIC ceiling estimates. The chip renders
       * amber (not a confident green) then.
       */
      degraded?: boolean
      gamesNeeded: number
      slotsRemaining: number
      autoBalanced: boolean
      /**
       * Strict-first FILL (backend-only): the STRICT recommended per-team max —
       * the count an all-hard generation produces (`constraints_relaxed=false`).
       * Equal to `gamesNeeded` translated per-team. Absent for the local estimate
       * and for non-fill targets.
       */
      recommendedMax?: number
      /**
       * Strict-first FILL (backend-only): the RELAXED per-team ceiling (soft
       * constraints relaxed). Exceeds `recommendedMax` when a satisfiable soft cap
       * would pack more games; the UI shows the "up to N if relaxed" line then.
       */
      capacityMax?: number
      /**
       * Strict-first FILL (backend-only): the RELAXED total games — the soft
       * ceiling, shown as the "up to N if relaxed" alternative to the primary
       * `gamesNeeded` (the strict total). Absent when there is no relaxed delta.
       */
      relaxedGamesNeeded?: number
    }
  | {
      kind: 'auto-balance-impossible'
      gamesNeeded: number
      closestBelow: number
      closestAbove: number
    }
  | {
      kind: 'infeasible'
      /**
       * Where the numbers came from. `'backend'` carries the server's
       * headroom-discounted recommendation; `'estimate'` is the local
       * pre-backend / network-failure fallback, which only knows the raw
       * ceiling (it cannot reproduce the server-side discount).
       */
      source: 'backend' | 'estimate'
      /**
       * Discounted, solver-safe recommended max games-per-team. `null` for
       * `'estimate'` — the client must NOT label a raw value "recommended".
       */
      recommendedMax: number | null
      /** Raw capacity ceiling (un-discounted); display/context only. */
      capacityMax: number
      gamesNeeded: number
      slotsAvailable: number
    }

/**
 * Compute feasibility verdict for one division's target games-per-team.
 *
 * Three states:
 *   - **feasible**: target achieves a valid schedule; auto-balance may
 *     have been applied internally if the math wasn't a clean integer
 *     across opponents (`autoBalanced` flag tells the caller).
 *   - **auto-balance-impossible**: BACKEND-ONLY now (`reason=balance_impossible`,
 *     mapped in `mapStructuredFeasibilityError`). This function no longer
 *     manufactures the verdict offline — it used to run a parity gate that
 *     rejected any target with an odd `n * gamesPerTeam`, but the backend rounds
 *     those UP rather than refusing them, so the gate was blocking targets
 *     generation produces happily (it killed the customer's 5 teams × 25).
 *   - **infeasible**: target exceeds slot capacity; UI should suggest
 *     the max achievable target.
 */
export function checkFeasibility(input: FeasibilityInput): FeasibilityResult {
  // `input.teamUids` is deliberately not destructured — nothing below reads it
  // (see the field's note on FeasibilityInput).
  const {
    divisionTeamCount: n,
    gamesPerTeam,
    totalAvailableSlots,
    alreadyAllocatedSlots = 0,
  } = input

  const opponents = Math.max(0, n - 1)
  // Each game involves 2 teams → games-per-team × N / 2 = total games.
  const gamesNeeded = opponents === 0 ? 0 : Math.ceil((n * gamesPerTeam) / 2)
  const slotsAvailableForThisDiv = Math.max(
    0,
    totalAvailableSlots - alreadyAllocatedSlots
  )

  // Hard capacity check first — slot scarcity beats balance considerations.
  if (gamesNeeded > slotsAvailableForThisDiv) {
    const maxGames = slotsAvailableForThisDiv
    // Raw capacity ceiling only — this local estimate cannot reproduce the
    // server-side headroom discount, so it never claims a "recommended" value.
    const capacityMax = n > 0 ? Math.floor((maxGames * 2) / n) : 0
    return {
      kind: 'infeasible',
      source: 'estimate',
      recommendedMax: null,
      capacityMax,
      gamesNeeded,
      slotsAvailable: slotsAvailableForThisDiv,
    }
  }

  // Trivial cases: 0 or 1 team, or zero target — nothing to balance.
  if (opponents === 0 || gamesPerTeam === 0) {
    return {
      kind: 'feasible',
      source: 'estimate',
      gamesNeeded,
      slotsRemaining: slotsAvailableForThisDiv - gamesNeeded,
      autoBalanced: false,
    }
  }

  // Clean division — every pair plays the same count.
  if (gamesPerTeam % opponents === 0) {
    return {
      kind: 'feasible',
      source: 'estimate',
      gamesNeeded,
      slotsRemaining: slotsAvailableForThisDiv - gamesNeeded,
      autoBalanced: false,
    }
  }

  // Doesn't divide evenly — the backend balances it, and the target is reachable
  // whether or not a PERFECTLY r-regular distribution exists.
  //
  // This used to run the parity gate (`buildAutoBalanceOverrides`) and report
  // `auto-balance-impossible` when it failed. But that gate only asks whether
  // every team can land on exactly N — and `(n * remainder) % 2` is algebraically
  // the parity of `n * N`, so it fails on precisely the odd-product targets the
  // backend's CEILING rounding now handles by giving one team N+1. It was
  // rejecting the customer's 5 teams × 25 as impossible while generation produced
  // it happily.
  //
  // `auto-balance-impossible` remains in the result union: the BACKEND can still
  // report `balance_impossible` for a division and that verdict is authoritative.
  // We simply no longer manufacture the verdict offline — which would mean
  // reproducing the backend's balancing algorithm in TypeScript.
  return {
    kind: 'feasible',
    source: 'estimate',
    gamesNeeded,
    slotsRemaining: slotsAvailableForThisDiv - gamesNeeded,
    autoBalanced: true,
  }
}

// ============================================================================
// Backend feasibility wire format
// ============================================================================

/**
 * One row in the request body posted to
 * `POST /scheduler/flights/{uid}/feasibility-check`.
 *
 * The contract is the unsaved modal proposal: explicit per-team target,
 * the base uniform games-per-matchup, and the unsaved per-pair overrides.
 * api2 turns this into in-memory `pair_matchup_count` constraints and
 * scheduler-python uses the same matrix-aware helpers as partition + solver
 * to compute the verdict — so a chip "feasible" reflects what generation
 * would actually produce, not a rough client estimate.
 */
export interface FeasibilityRequestDivision {
  uid: string
  games_per_team_target: number
  base_games_per_matchup: number
  pair_matchup_counts: { team_uids: [string, string]; count: number }[]
}

/**
 * The shape of one verdict row returned in `data.divisions[*]` from the
 * feasibility-check endpoint. Backed by the Pydantic
 * `DivisionFeasibility` model in scheduler-python.
 */
export interface BackendFeasibilityVerdict {
  division_uid: string
  feasible: boolean
  games_per_team_target: number
  /** Discounted, solver-safe recommended max games-per-team. */
  games_per_team_max: number
  /**
   * Raw capacity ceiling (un-discounted). Additive/optional: absent when the
   * scheduler image predates this field — callers fall back to
   * `games_per_team_max`.
   */
  games_per_team_capacity_max?: number
  games_needed: number
  /**
   * Strict-first FILL: the RELAXED total games (soft ceiling) for the secondary
   * "up to N if rules are relaxed" line. Additive/optional: absent when the
   * scheduler image predates this field or the division isn't a recomputed fill.
   */
  relaxed_games_needed?: number
  slots_estimated_for_division: number
  reason: 'capacity' | 'ok' | 'balance_impossible'
}

/**
 * Build the wire body for the feasibility-check endpoint from the modal's
 * per-division entries. `entry.matrix` keys are canonical pipe-delimited
 * pairs (`a|b`, sorted) — split them back into the contract shape.
 */
export function buildFeasibilityDivisionsBody(
  entries: {
    div: { id: string }
    entry: { matrix: MatchupMatrix; gamesPerMatchup: number }
    perTeam: number
  }[]
): FeasibilityRequestDivision[] {
  return entries.map(({ div, entry, perTeam }) => ({
    uid: div.id,
    games_per_team_target: perTeam,
    base_games_per_matchup: entry.gamesPerMatchup,
    pair_matchup_counts: Object.entries(entry.matrix).map(([key, count]) => {
      const [a, b] = key.split('|')
      return { team_uids: [a, b] as [string, string], count }
    }),
  }))
}

/**
 * Map a backend verdict to the modal's `FeasibilityResult` chip state.
 *
 * `balance_impossible` is the new server reason — it covers proposals
 * whose pair counts can't realize the requested per-team target (parity
 * fails). For now we derive `closestBelow` / `closestAbove` locally from
 * `buildAutoBalanceOverrides`; surfacing those from the server is a
 * follow-up.
 *
 * `opts.degraded` mirrors the response-level `feasibility_degraded` flag
 * (D1/D4, 2026-07 audit): scheduler-python's partition refinement failed and
 * the verdict is an OPTIMISTIC ceiling estimate — a feasible verdict renders
 * amber then, never a confident green.
 */
export function mapBackendVerdictToFeasibility(
  verdict: BackendFeasibilityVerdict,
  teamUids: string[],
  opts?: { degraded?: boolean }
): FeasibilityResult {
  if (verdict.feasible) {
    return {
      kind: 'feasible',
      source: 'backend',
      degraded: opts?.degraded ?? false,
      // PRIMARY total: for a strict-first fill this is the STRICT (all-hard)
      // count — what generation produces with constraints_relaxed=false.
      gamesNeeded: verdict.games_needed,
      slotsRemaining:
        verdict.slots_estimated_for_division - verdict.games_needed,
      autoBalanced: false,
      // Strict-first FILL dual numbers (backend-only; undefined for non-fill /
      // older images): strict recommended per-team, relaxed per-team ceiling, and
      // the relaxed total for the "up to N if relaxed" secondary line.
      recommendedMax: verdict.games_per_team_max,
      capacityMax:
        verdict.games_per_team_capacity_max ?? verdict.games_per_team_max,
      relaxedGamesNeeded: verdict.relaxed_games_needed,
    }
  }
  if (verdict.reason === 'balance_impossible') {
    const proposal = buildAutoBalanceOverrides(
      teamUids,
      verdict.games_per_team_target
    )
    return {
      kind: 'auto-balance-impossible',
      gamesNeeded: verdict.games_needed,
      closestBelow: proposal?.closestUniformBelow ?? 0,
      closestAbove: proposal?.closestUniformAbove ?? 0,
    }
  }
  return {
    kind: 'infeasible',
    source: 'backend',
    recommendedMax: verdict.games_per_team_max,
    // Older scheduler images omit the capacity field — fall back to the
    // recommended max so the ceiling line just collapses (ceiling == recommended).
    capacityMax:
      verdict.games_per_team_capacity_max ?? verdict.games_per_team_max,
    gamesNeeded: verdict.games_needed,
    slotsAvailable: verdict.slots_estimated_for_division,
  }
}

/**
 * Whether to render the strict-first FILL secondary line ("…or up to N games if
 * rules are relaxed").
 *
 * Gate on the TOTAL delta — the relaxed ceiling strictly above the strict total —
 * NOT the floored per-team maxes. Two different totals can floor to the SAME
 * per-team (e.g. 5 teams: strict 11 and relaxed 12 both → 4/team), so a per-team
 * gate would wrongly hide a real relaxed alternative (and leak the misleading
 * "slots free" copy). Shared by the matchup chip and the generate-modal summary so
 * the two cannot drift apart again. The per-team value still appears IN the copy;
 * it just isn't the visibility gate.
 */
export function showRelaxedSecondary(
  gamesNeeded: number,
  relaxedGamesNeeded: number | null | undefined
): boolean {
  return relaxedGamesNeeded != null && relaxedGamesNeeded > gamesNeeded
}

/**
 * Display decision for the FEASIBLE chip's strict-first FILL dual numbers.
 *
 * - `showRelaxed`: render the secondary "…up to N if rules are relaxed" line —
 *   true when the relaxed TOTAL exceeds the strict total (see
 *   {@link showRelaxedSecondary}).
 * - `showSlotsFree`: render the "X of Y slots free" phrase. Suppressed exactly
 *   when `showRelaxed` is true — there, "slots free" is the RELAXED ceiling minus
 *   the STRICT total (e.g. 252 of 390), which reads as "underfilled" even though
 *   the strict schedule is FULL at its all-hard ceiling. The relaxed secondary
 *   line carries the honest spare-capacity context instead.
 */
export function feasibleChipDisplay(result: {
  gamesNeeded: number
  relaxedGamesNeeded?: number
}): { showRelaxed: boolean; showSlotsFree: boolean } {
  const showRelaxed = showRelaxedSecondary(
    result.gamesNeeded,
    result.relaxedGamesNeeded
  )
  return { showRelaxed, showSlotsFree: !showRelaxed }
}

// ============================================================================
// Per-team ↔ division-total conversion (Matchup Config input mode toggle)
// ============================================================================
//
// "Games per team" is the canonical product contract. The modal also lets
// admins enter a division total instead, mathematically linked by
//   total = teamCount * gamesPerTeam / 2
// (each game contributes one game to two teams). The toggle is local UI
// state — only the resolved per-team value reaches the backend.
//
// Parity: when teamCount is odd, gamesPerTeam must be even for total to
// land on an integer. When teamCount is even, every integer gamesPerTeam
// produces an integer total.

/**
 * Division total games derived from a per-team target.
 *
 * Returns 0 when teamCount < 2 (no pairs ⇒ no games). Assumes
 * `gamesPerTeam` is non-negative; callers clamp at the input.
 */
export function divisionTotalFromGamesPerTeam(
  teamCount: number,
  gamesPerTeam: number
): number {
  if (teamCount < 2 || gamesPerTeam < 0) {
    return 0
  }
  // Each pair contributes one game to two teams; total = ceil(n * perTeam / 2).
  // CEIL, matching the backend: when the product is ODD the halves don't divide,
  // and rounding down would leave one team below the requested target. The extra
  // game puts a single team at perTeam + 1 instead — nobody short.
  return Math.ceil((teamCount * gamesPerTeam) / 2)
}

/**
 * Inverse of `divisionTotalFromGamesPerTeam`.
 *
 * Returns null when the requested total cannot split evenly across teams
 * (i.e. `total * 2` is not divisible by `teamCount`, or inputs are
 * degenerate). Callers route a null result to the "Invalid total" chip
 * and use `closestBalancedDivisionTotals` to surface achievable
 * neighbours.
 */
export function gamesPerTeamFromDivisionTotal(
  teamCount: number,
  totalGames: number
): number | null {
  if (teamCount < 2 || totalGames < 0) {
    return null
  }
  const numerator = totalGames * 2
  if (numerator % teamCount !== 0) {
    return null
  }
  return numerator / teamCount
}

/**
 * Closest valid division totals for an invalid input.
 *
 * Snaps the implied per-team value to the nearest valid integers
 * below/above (parity-aware for odd team counts) and back-computes their
 * totals. Returns null when the input is already valid (no suggestion
 * needed) or when inputs are degenerate.
 *
 * Examples:
 *   - 6 teams, 50 → { below: { total: 48, perTeam: 16 },
 *                     above: { total: 51, perTeam: 17 } }
 *   - 5 teams, 11 → { below: { total: 10, perTeam: 4 },
 *                     above: { total: 15, perTeam: 6 } }   (parity-aware)
 */
export function closestBalancedDivisionTotals(
  teamCount: number,
  totalGames: number
): {
  below: { total: number; perTeam: number }
  above: { total: number; perTeam: number }
} | null {
  if (teamCount < 2 || totalGames < 0) {
    return null
  }
  if (gamesPerTeamFromDivisionTotal(teamCount, totalGames) !== null) {
    return null
  }

  // Smallest integer step on perTeam that preserves parity. For odd team
  // counts perTeam must be even, so the step is 2; for even team counts
  // any integer perTeam is valid, step 1.
  const step = teamCount % 2 === 1 ? 2 : 1

  const perTeamRaw = (totalGames * 2) / teamCount

  // Snap perTeamRaw to the nearest multiple of `step` below and above.
  let perTeamBelow = Math.floor(perTeamRaw / step) * step
  if (perTeamBelow < 0) {
    perTeamBelow = 0
  }
  let perTeamAbove = perTeamBelow + step
  // If perTeamRaw happens to land exactly on a valid value (shouldn't
  // happen because gamesPerTeamFromDivisionTotal already returned null,
  // but guard anyway) bump above by one step.
  if (perTeamAbove === perTeamRaw) {
    perTeamAbove += step
  }

  return {
    below: {
      perTeam: perTeamBelow,
      total: divisionTotalFromGamesPerTeam(teamCount, perTeamBelow),
    },
    above: {
      perTeam: perTeamAbove,
      total: divisionTotalFromGamesPerTeam(teamCount, perTeamAbove),
    },
  }
}

// ---------------------------------------------------------------------------
// Target contract (the canonical wizard generation control). Replaces the
// per-pair auto-balance path as the SOURCE OF TRUTH for explicit games-per-team
// / division-total. The backend (scheduler-python) derives balanced per-pair
// counts from the target — so the client no longer needs to expand a target
// into pair_matchup_count rows, and Division Total can be an exact value that
// does NOT divide evenly across teams (the backend balances ±1).
// ---------------------------------------------------------------------------

export type SchedulerTargetMode =
  | 'fill_feasible_slots'
  | 'games_per_team'
  | 'division_total'

/** Persisted target shape sent to POST /division-targets. */
export interface DivisionTargetBody {
  division_uid: string
  target_mode: SchedulerTargetMode
  target_number: number | null
}

/** Per-division target shape sent inline to /feasibility-check. */
export interface FeasibilityTargetDivision {
  uid: string
  target_mode: SchedulerTargetMode
  target_number: number | null
}

/**
 * Resolve the explicit per-division target from the modal's input mode +
 * value. The modal toggle maps directly to the target contract:
 *   - `per_team`        → games_per_team (target_number = N games per team)
 *   - `division_total`  → division_total (target_number = exact total, uneven
 *                          allowed; the backend balances)
 * A non-positive / missing number means "no explicit target" → fill_feasible_slots
 * (target_number null), the season-filling default.
 */
export function resolveDivisionTarget(input: {
  inputMode: 'per_team' | 'division_total'
  perTeam: number
  divisionTotal: number | null
}): { target_mode: SchedulerTargetMode; target_number: number | null } {
  const { inputMode, perTeam, divisionTotal } = input

  if (inputMode === 'division_total') {
    const total =
      typeof divisionTotal === 'number' ? Math.floor(divisionTotal) : NaN
    if (Number.isFinite(total) && total >= 1) {
      return { target_mode: 'division_total', target_number: total }
    }
    return { target_mode: 'fill_feasible_slots', target_number: null }
  }

  const n = Math.floor(perTeam)
  if (Number.isFinite(n) && n >= 1) {
    return { target_mode: 'games_per_team', target_number: n }
  }
  return { target_mode: 'fill_feasible_slots', target_number: null }
}

/**
 * Build the POST /division-targets body from resolved per-division targets.
 */
export function buildDivisionTargetsBody(
  entries: {
    divisionId: string
    target: { target_mode: SchedulerTargetMode; target_number: number | null }
  }[]
): DivisionTargetBody[] {
  return entries.map(({ divisionId, target }) => ({
    division_uid: divisionId,
    target_mode: target.target_mode,
    target_number: target.target_number,
  }))
}

/** A saved per-division target row from GET /division-targets. */
export interface SavedDivisionTargetRow {
  division_uid: string
  target_mode: SchedulerTargetMode
  target_number: number | null
}

/**
 * Normalize the GET /division-targets response into saved target rows.
 *
 * The endpoint wraps rows in `{ data: [...] }`, and `req` returns the response
 * whose `.data` is that body — so callers receive `{ data: [...] }` and must
 * unwrap (the `data?.data ?? data` convention used across the scheduler hooks).
 * Returns `[]` for any non-array / malformed shape so the modal falls back to
 * defaults instead of throwing.
 */
export function parseDivisionTargetsResponse(
  data: unknown
): SavedDivisionTargetRow[] {
  const rows =
    data && typeof data === 'object' && 'data' in data
      ? (data as { data: unknown }).data
      : data
  if (!Array.isArray(rows)) {
    return []
  }
  return rows.filter(
    (r): r is SavedDivisionTargetRow =>
      !!r &&
      typeof r === 'object' &&
      typeof (r as SavedDivisionTargetRow).division_uid === 'string' &&
      typeof (r as SavedDivisionTargetRow).target_mode === 'string'
  )
}

/**
 * Build the inline `divisions` body for /feasibility-check from resolved
 * per-division targets (the target contract — NOT the legacy proposal shape).
 */
export function buildFeasibilityTargetsBody(
  entries: {
    divisionId: string
    target: { target_mode: SchedulerTargetMode; target_number: number | null }
  }[]
): FeasibilityTargetDivision[] {
  return entries.map(({ divisionId, target }) => ({
    uid: divisionId,
    target_mode: target.target_mode,
    target_number: target.target_number,
  }))
}

// ============================================================================
// Structured feasibility-error mapping (D5 client seam, 2026-07 review)
// ============================================================================

/**
 * Shape of api2's structured feasibility 422 body. `req()` surfaces 4xx
 * bodies TOP-LEVEL on the thrown error (5xx go under `err.bodyData`), so the
 * fields live directly on the caught object.
 */
interface StructuredFeasibilityError {
  error?: string
  message?: string
  detail?: {
    code?: string
    message?: string
    divisions?: {
      division_uid?: string
      games_needed?: number
      achievable_games?: number
      max_games_per_team?: number
    }[]
  }
}

/**
 * Map a STRUCTURED scheduler rejection into per-division RED backend
 * verdicts, or return `null` when the error carries no usable structure
 * (network failure / unknown shape) — callers then keep the labeled local
 * estimate, which is the documented offline fallback.
 *
 * Pre-fix the modal cleared the backend map on ANY error, so a structured
 * 422 (api2 forwards scheduler-python's detail verbatim since D5) silently
 * fell back to the OPTIMISTIC local estimate — the exact disconnect D5
 * closed on the server side.
 */
export function mapStructuredFeasibilityError(
  err: unknown
): Map<string, FeasibilityResult> | null {
  const e = err as StructuredFeasibilityError | null
  const detail = e?.detail
  if (
    !detail ||
    !Array.isArray(detail.divisions) ||
    detail.divisions.length === 0
  ) {
    return null
  }
  const next = new Map<string, FeasibilityResult>()
  for (const d of detail.divisions) {
    const uid = d.division_uid
    if (!uid) {
      continue
    }
    next.set(uid, {
      kind: 'infeasible',
      source: 'backend',
      // The scheduler's structured detail carries the achievable per-team
      // max — the same Apply-value the red chip offers.
      recommendedMax: d.max_games_per_team ?? null,
      capacityMax: d.max_games_per_team ?? 0,
      gamesNeeded: d.games_needed ?? 0,
      slotsAvailable: d.achievable_games ?? 0,
    })
  }
  return next.size > 0 ? next : null
}

/**
 * What we may honestly promise a user who set a `games_per_team` target.
 *
 * The backend rounds the division total UP, so with no pair overrides every team
 * reaches the target and — when `teamCount * target` is odd — exactly one team
 * plays one extra game.
 *
 * That promise does NOT survive a pair override. `PAIR_MATCHUP_COUNT` is a HARD
 * constraint and WINS ("explicit PAIR_MATCHUP_COUNT overrides win"), while the
 * division total is still `ceil(n * N / 2)` and takes no account of what the
 * override CONSUMES. Measured on 5 teams × 25 with an A–B override of 20, three
 * teams land on 19. Ceiling does not fix that — so when any override is enabled
 * we say overrides take priority instead of promising "at least N".
 *
 * We deliberately do NOT try to predict the resulting per-team numbers: that
 * would mean reproducing the backend's balancing algorithm in TypeScript. We
 * branch on the PRESENCE of an enabled override, nothing more.
 *
 * (The `games_per_team` vs `pair_matchup_count` precedence itself is an open
 * product question, ticketed for James.)
 */
export function gamesPerTeamPromiseCopy(
  hasEnabledPairOverride: boolean,
  target?: number | null
): string {
  if (hasEnabledPairOverride) {
    return 'Pair-specific overrides take priority. Individual team totals may differ from the games-per-team target.'
  }
  const n = typeof target === 'number' && target > 0 ? `${target}` : 'this many'
  return `At least ${n} games per team; with an odd number of team appearances, one team plays one extra.`
}
